import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  notificationsService,
  type CreateNotificationDto,
  type NotificationFilters,
  type BroadcastNotificationDto,
} from '@/services/notifications';
import type { DeliveryLogsResponse } from '@/services/notifications';
import { QUERY_KEYS } from './queryKeys';
import { getApiErrorMessage } from '@/utils/apiError';
import toast from 'react-hot-toast';

export const useNotificationsQuery = (filters?: NotificationFilters) => {
  return useQuery({
    queryKey: QUERY_KEYS.notifications.list(filters),
    queryFn: () => notificationsService.getNotifications(filters),
  });
};

export const useNotificationPreferences = () => {
  return useQuery({
    queryKey: QUERY_KEYS.notifications.preferences(),
    queryFn: () => notificationsService.getPreferences(),
    staleTime: 5 * 60_000,
  });
};

export const useUnreadCount = () => {
  return useQuery({
    queryKey: QUERY_KEYS.notifications.unreadCount(),
    queryFn: () => notificationsService.getUnreadCount(),
    refetchInterval: 30_000, // Poll every 30s for badge updates
  });
};

export const useCreateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNotificationDto) => notificationsService.createNotification(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.unreadCount() });
      toast.success('Notification sent');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to send notification'));
    },
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => notificationsService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.unreadCount() });
    },
  });
};

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.unreadCount() });
      toast.success('All notifications marked as read');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to mark all as read'));
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => notificationsService.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.unreadCount() });
      toast.success('Notification deleted');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete notification'));
    },
  });
};

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsService.updatePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.preferences() });
      toast.success('Preferences updated');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to update preferences'));
    },
  });
};

export const useBroadcastNotification = () => {
  return useMutation({
    mutationFn: (data: BroadcastNotificationDto) =>
      notificationsService.broadcastNotification(data),
    onSuccess: () => {
      toast.success('Broadcast sent successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to send broadcast'));
    },
  });
};

// ── SUPER_ADMIN delivery audit ─────────────────────────────────────────────

export const useDeliveryLogs = (params?: {
  page?: number;
  limit?: number;
  status?: string;
  channel?: string;
}) => {
  return useQuery<DeliveryLogsResponse>({
    queryKey: QUERY_KEYS.notifications.deliveryLogs(params as Record<string, unknown>),
    queryFn: () => notificationsService.getDeliveryLogs(params),
    staleTime: 15_000,
  });
};

export const useResendDelivery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (logId: number) => notificationsService.resendDelivery(logId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.deliveryLogs() });
      toast.success('Notification re-queued for delivery');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to resend notification'));
    },
  });
};
